/**
 * Client-Side Initialization Service
 *
 * Replaces server-side middleware logic for CSR architecture.
 * Handles:
 * - Hostname detection and mode determination
 * - Partner config fetching
 * - SSO authentication
 * - Cookie/localStorage management
 * - Client-side redirects
 */

import { Cookie, Mode, QueryParam, SsoAuthParamData } from "~/types";
import { HUBBLE_CLIENT_ID } from "~/data/config";
import { isHostOnlyForIframeEmbed } from "~/data/partner_config";
import { base64Decode, base64Encode } from "~/utils/string_utils";
import { clientRepo } from "~/server/apis/client_repo";
import { RnrPortalsJson } from "~/server/types/client";
import { getStaticRnrPortalsJson } from "~/server/static";
import { SDKInitParams } from "~/server/types/auth";
import { getStorage, setStorage, removeStorage } from "~/utils/storage";
import { config } from "~/data/config";

// Memoization cache for partner configs
const configCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

export interface AppInitContext {
  clientId: string;
  clientSecret?: string;
  mode: Mode;
  sessionId?: string;
  rnrPortalsJson?: RnrPortalsJson;
  clientLogoUrl?: string;
  authMethod?: string;
  partnerName?: string;
}

/**
 * Main initialization function - replaces all middleware logic
 */
export async function initializeApp(): Promise<AppInitContext> {
  console.log("[ClientInit] Starting app initialization...");

  // Get current URL and hostname
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  const hostname = getClientHostname(url, searchParams.get("domain"));

  console.log("[ClientInit] Hostname:", hostname);

  // Handle client-side redirects first
  const redirect = handleRedirects(hostname, url);
  if (redirect) {
    console.log("[ClientInit] Redirecting to:", redirect);
    window.location.href = redirect;
    // Return default context while redirecting
    return {
      clientId: HUBBLE_CLIENT_ID,
      mode: "hubble",
    };
  }

  // Check existing storage (localStorage primary, cookie fallback)
  let storedClientId = getStorage(Cookie.ClientId);
  let storedClientSecret = getStorage(Cookie.ClientSecret);
  let storedMode = getStorage(Cookie.Mode) as Mode | undefined;
  let storedSessionId = getStorage(Cookie.SessionId);

  console.log("[ClientInit] Existing storage:", {
    clientId: !!storedClientId,
    clientSecret: !!storedClientSecret,
    mode: storedMode,
    sessionId: !!storedSessionId,
  });

  // Handle SSO auth fallback (for Raisoni mobile app issue)
  const ssoAuthParam = searchParams.get(QueryParam.SsoAuth);
  if (!storedClientId && !storedClientSecret && !storedMode && ssoAuthParam) {
    console.log("[ClientInit] Processing SSO auth from query param");
    const ssoAuthData = JSON.parse(
      base64Decode(ssoAuthParam)
    ) as SsoAuthParamData;

    setStorageWithAuthData(ssoAuthData);

    storedClientId = ssoAuthData.clientId;
    storedClientSecret = ssoAuthData.clientSecret;
    storedMode = ssoAuthData.mode as Mode;
    storedSessionId = ssoAuthData.sid;
  }

  // Handle partner SSO (clientId + clientSecret in query params)
  const clientIdParam = searchParams.get("clientId");
  const clientSecretParam = searchParams.get("clientSecret");
  const tokenParam = searchParams.get("token");

  if (clientIdParam && clientSecretParam && !url.pathname.includes("classic")) {
    console.log("[ClientInit] Performing SSO login...");
    try {
      const sdkAuthData = await clientRepo.performSSOLogin({
        clientId: clientIdParam,
        clientSecret: clientSecretParam,
        token: tokenParam,
      } as SDKInitParams);

      const sessionId = sdkAuthData.sessionId;

      // Set storage
      setStorageForAuth({
        sessionId,
        clientId: clientIdParam,
        clientSecret: clientSecretParam,
        mode: "rnr",
      });

      storedClientId = clientIdParam;
      storedClientSecret = clientSecretParam;
      storedMode = "rnr";
      storedSessionId = sessionId;

      // Redirect with sso-auth param as fallback
      const ssoAuth: SsoAuthParamData = {
        sid: sessionId,
        clientId: clientIdParam,
        clientSecret: clientSecretParam,
        mode: "rnr",
      };
      const ssoAuthEncoded = base64Encode(JSON.stringify(ssoAuth));
      const newUrl = new URL(window.location.origin);
      newUrl.searchParams.set(QueryParam.SsoAuth, ssoAuthEncoded);

      console.log("[ClientInit] SSO login successful, redirecting...");
      window.location.replace(newUrl.toString());

      // Return context while redirecting
      return {
        clientId: clientIdParam,
        clientSecret: clientSecretParam,
        mode: "rnr",
        sessionId,
      };
    } catch (e) {
      console.error("[ClientInit] SSO login failed:", e);
    }
  }

  // Determine mode and fetch partner config
  let context: AppInitContext = {
    clientId: HUBBLE_CLIENT_ID,
    mode: "hubble",
    sessionId: storedSessionId ?? undefined,
  };

  let rnrPortalsJson: RnrPortalsJson | undefined;

  try {
    // Check if clientId in query params (for iframe deep linking)
    if (clientIdParam && !clientSecretParam) {
      console.log("[ClientInit] Client ID from query param:", clientIdParam);
      rnrPortalsJson = await getMemoizedRnrPortalJson(clientIdParam);
      context.mode = "rnr";
      context.clientId = clientIdParam;
    } else if (isSquidPortal(hostname)) {
      console.log("[ClientInit] Squid mode detected");
      context.mode = "squid";
    } else if (!isHubbleMode(hostname)) {
      // Fetch partner details by hostname
      console.log(
        "[ClientInit] Fetching partner config for hostname:",
        hostname
      );
      const partnerDetails = await getMemoizedPartnerConfig(hostname);

      if (partnerDetails) {
        console.log("[ClientInit] Partner config found:", partnerDetails);

        if (partnerDetails.mode === "RNR") {
          rnrPortalsJson = await getMemoizedRnrPortalJson(
            partnerDetails.clientId
          );
        }

        context.mode = (
          partnerDetails.mode ??
          storedMode ??
          "hubble"
        ).toLowerCase() as Mode;
        context.clientLogoUrl = partnerDetails.clientLogoUrl;
        context.authMethod = partnerDetails.authMethod;
        context.partnerName = partnerDetails.partnerName;

        // Use stored values if available, otherwise use API response
        if (storedClientId && storedClientSecret) {
          context.clientId = storedClientId;
          context.clientSecret = storedClientSecret;
        } else {
          context.clientId = partnerDetails.clientId;
          context.clientSecret = partnerDetails.appSecret;
        }
      }
    }
  } catch (error) {
    console.error("[ClientInit] Failed to fetch partner details:", error);
    // Default to hubble mode on error
    if (hostname.endsWith("hubble-rewards.com")) {
      // Show error page for hubble-rewards.com domains
      document.body.innerHTML = generateErrorPage();
      throw error;
    }
  }

  // Set all storage
  setStorageForInitializedApp(context, rnrPortalsJson);

  // Set mock repo from env
  if (import.meta.env.VITE_MOCK_REPO) {
    setStorage(Cookie.MockRepo, import.meta.env.VITE_MOCK_REPO);
  }

  console.log("[ClientInit] Initialization complete:", context);

  return {
    ...context,
    rnrPortalsJson,
  };
}

/**
 * Get hostname with support for domain override query param
 */
function getClientHostname(url: URL, domainParam: string | null): string {
  if (domainParam) {
    switch (domainParam) {
      case "www":
        return "www.dev.myhubble.money";
      case "offers":
        return "offers.dev.myhubble.money";
      case "gifting":
        return "gifting.dev.myhubble.money";
      case "rnr":
        return "acme.dev.myhubble.money";
      case "rnr-axis":
        return "axis-code.dev.hubble-rewards.com";
      case "rnr-td":
        return "tata-digital.dev.hubble-rewards.com";
      case "rnr-gp":
        return "godrej-properties.dev.hubble-rewards.com";
      case "rnr-blb":
        return "brand-look-book.dev.hubble-rewards.com";
      case "squid":
        return "squid.dev.hubble-rewards.com";
      default:
        return url.hostname;
    }
  }

  // Check environment variables (for local development)
  if (import.meta.env.VITE_WWW === "1") return "www.dev.myhubble.money";
  if (import.meta.env.VITE_OFFERS === "1") return "offers.dev.myhubble.money";
  if (import.meta.env.VITE_GIFTING === "1") return "gifting.dev.myhubble.money";
  if (import.meta.env.VITE_RNR === "1") return "acme.dev.myhubble.money";
  if (import.meta.env.VITE_RNR_AXIS === "1")
    return "axis-code.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_TD === "1")
    return "tata-digital.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_BAJAJ === "1")
    return "bajaj-code.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_RAISONI === "1")
    return "privilege.test.raisoni.net";
  if (import.meta.env.VITE_RNR_GODREJ_PROPERTIES === "1")
    return "godrej-properties.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_MONEY_CONTROL === "1")
    return "privilegesmoneycontrol.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_BUDDY_DEALS === "1")
    return "shop.dev.buddydeal.in";
  if (import.meta.env.VITE_RNR_BLB === "1")
    return "brand-look-book.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_NIELSEN === "1")
    return "nielseniq-offers.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_PORTAL_TESTING === "1")
    return "testing.dev.hubble-rewards.com";
  if (import.meta.env.VITE_RNR_VH_REWARDS === "1")
    return config.env === "Prod"
      ? "visit-health-rewards.hubble-rewards.com"
      : "visit-health-rewards.dev.hubble-rewards.com";
  if (import.meta.env.VITE_SQUID === "1") return "squid.dev.hubble-rewards.com";
  return url.hostname;
}

/**
 * Handle client-side redirects (replaces redirect middleware logic)
 * Returns the redirect URL if a redirect is needed, null otherwise
 */
function handleRedirects(hostname: string, url: URL): string | null {
  const pathname = url.pathname;
  const search = url.search;

  // Business domain redirects
  if (hostname === "business.myhubble.money") {
    return `https://www.myhubble.money${pathname}${search}`;
  }
  if (hostname === "business.dev.myhubble.money") {
    return `https://www.dev.myhubble.money${pathname}${search}`;
  }

  // Old route redirects
  if (pathname.includes("brand-collection")) {
    return `${pathname.replace("brand-collection", "m")}${search}`;
  }
  if (pathname.includes("personalised-gift")) {
    return `${pathname.replace("personalised-gift", "pluto")}${search}`;
  }

  // Rewards domain redirect
  if (
    hostname === "rewards.myhubble.money" &&
    !url.toString().includes("no-redirect")
  ) {
    const searchParams = new URLSearchParams(search);
    searchParams.set("no-redirect", "1");
    return `https://www.myhubble.money${pathname}?${searchParams.toString()}`;
  }

  // Corporate gifting redirect
  if (hostname === "www.myhubble.money" && pathname === "/corporate-gifting") {
    return `https://www.myhubble.money/bulk-orders`;
  }

  // Rewards store redirects
  if (
    hostname === "www.myhubble.money" &&
    pathname === "/rewards-store" &&
    !url.toString().includes("no-redirect")
  ) {
    return `https://offers.myhubble.money/rewards-store?no-redirect=1`;
  }
  if (
    hostname === "www.dev.myhubble.money" &&
    pathname === "/rewards-store" &&
    !url.toString().includes("no-redirect")
  ) {
    return `https://offers.dev.myhubble.money/rewards-store?no-redirect=1`;
  }

  // Universe redirect
  if (
    hostname === "universe.myhubble.money" &&
    !url.toString().includes("no-redirect")
  ) {
    const searchParams = new URLSearchParams(search);
    searchParams.set("no-redirect", "1");
    return `https://www.myhubble.money${pathname}?${searchParams.toString()}`;
  }

  // Legacy Squid redirects
  if (
    hostname === "demo.hubble-rewards.com" ||
    hostname === "stores.hubble-rewards.com"
  ) {
    return "https://store.hubble-rewards.com";
  }

  // Squid partner portal redirects
  if (SQUID_PARTNER_PORTALS.has(hostname)) {
    return "https://squid.hubble-rewards.com";
  }

  return null;
}

/**
 * Check if hostname is Hubble mode
 */
function isHubbleMode(hostname: string): boolean {
  const hubbleModeHosts = new Set([
    "www.myhubble.money",
    "www.dev.myhubble.money",
    "vouchers.myhubble.money",
    "vouchers.dev.myhubble.money",
    "offers.myhubble.money",
    "offers.dev.myhubble.money",
    "gifting.myhubble.money",
    "gifting.dev.myhubble.money",
    "localhost",
  ]);
  return hubbleModeHosts.has(hostname);
}

/**
 * Check if hostname is Squid portal
 */
function isSquidPortal(hostname: string): boolean {
  const squidPortalHosts = new Set([
    "squid.hubble-rewards.com",
    "squid.dev.hubble-rewards.com",
  ]);
  return squidPortalHosts.has(hostname);
}

/**
 * Memoized fetch for RNR portal JSON
 */
async function getMemoizedRnrPortalJson(
  clientId: string
): Promise<RnrPortalsJson> {
  const cacheKey = `rnr_${clientId}`;
  const cached = configCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log("[ClientInit] Using cached RNR portal JSON");
    return cached.data;
  }

  console.log("[ClientInit] Fetching RNR portal JSON:", clientId);
  const data = await getStaticRnrPortalsJson(clientId);
  configCache.set(cacheKey, { data, timestamp: Date.now() });
  return data;
}

/**
 * Memoized fetch for partner config
 */
async function getMemoizedPartnerConfig(hostname: string): Promise<any> {
  const cacheKey = `partner_${hostname}`;
  const cached = configCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log("[ClientInit] Using cached partner config");
    return cached.data;
  }

  console.log("[ClientInit] Fetching partner config for:", hostname);
  const data = await clientRepo.getMinimalClientConfigByPortal(hostname);
  configCache.set(cacheKey, { data, timestamp: Date.now() });
  return data;
}

/**
 * Set storage for authenticated session
 */
function setStorageForAuth(data: {
  sessionId: string;
  clientId: string;
  clientSecret: string;
  mode: Mode;
}) {
  setStorage(Cookie.SessionId, data.sessionId);
  setStorage(Cookie.ClientId, data.clientId);
  setStorage(Cookie.ClientSecret, data.clientSecret);
  setStorage(Cookie.Mode, data.mode);

  console.log("[ClientInit] Set auth storage");
}

/**
 * Set storage with SSO auth data
 */
function setStorageWithAuthData(ssoAuthData: SsoAuthParamData) {
  setStorageForAuth({
    sessionId: ssoAuthData.sid,
    clientId: ssoAuthData.clientId,
    clientSecret: ssoAuthData.clientSecret,
    mode: ssoAuthData.mode as Mode,
  });
  console.log("[ClientInit] Set storage from SSO auth data");
}

/**
 * Set storage for initialized app context
 */
function setStorageForInitializedApp(
  context: AppInitContext,
  rnrPortalsJson?: RnrPortalsJson
) {
  setStorage(Cookie.ClientId, context.clientId);
  setStorage(Cookie.Mode, context.mode);

  if (context.clientSecret) {
    setStorage(Cookie.ClientSecret, context.clientSecret);
  }

  setStorage("isDarkTheme", (rnrPortalsJson?.isDarkTheme ?? false).toString());
  setStorage("rnrPortalsJson", JSON.stringify(rnrPortalsJson ?? {}));

  console.log("[ClientInit] Set app context storage");
}

/**
 * Generate error page HTML
 */
function generateErrorPage(): string {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Page Not Found</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100vh;
            margin: 0;
            background: #f5f5f5;
          }
          .error-container {
            text-align: center;
            padding: 2rem;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          h1 { color: #333; margin: 0 0 1rem; }
          p { color: #666; }
        </style>
      </head>
      <body>
        <div class="error-container">
          <h1>404</h1>
          <p>Page doesn't exist.</p>
        </div>
      </body>
    </html>
  `;
}

/**
 * Old Squid partner portal URLs that should redirect
 */
const SQUID_PARTNER_PORTALS = new Set([
  "abc-private-limited.hubble-rewards.com",
  "proc-temp-test.hubble-rewards.com",
  "pro-retails.hubble-rewards.com",
  "swapnil-test.hubble-rewards.com",
  "flash-monk.hubble-rewards.com",
  "ulink.hubble-rewards.com",
  "procurement.hubble-rewards.com",
  "xyz.hubble-rewards.com",
  "proc-temp-2.hubble-rewards.com",
  "saumil-test.hubble-rewards.com",
  "corporate-konnexions.hubble-rewards.com",
  "marquis-technologies.hubble-rewards.com",
  "a2bgifting.hubble-rewards.com",
  "meteoroidbox.hubble-rewards.com",
  "lifestyleinternational.hubble-rewards.com",
  "augustventure.hubble-rewards.com",
  "inentertainment.hubble-rewards.com",
  "extentiainformation.hubble-rewards.com",
  "ceatlimited.hubble-rewards.com",
  "bolttechdevice.hubble-rewards.com",
  "devonsoftware.hubble-rewards.com",
  "petro-it.hubble-rewards.com",
  "allindia-difficultairways.hubble-rewards.com",
  "mediahermits.hubble-rewards.com",
  "synopsys.hubble-rewards.com",
  "ketan.hubble-rewards.com",
  "rohitdeshmukh.hubble-rewards.com",
  "hariprasad-mallidi.hubble-rewards.com",
  "cuirallysolutions.hubble-rewards.com",
  "nahatadatacom.hubble-rewards.com",
  "av-retail.hubble-rewards.com",
  "conceptandsolutionevent.hubble-rewards.com",
  "atgbusiness-solutions.hubble-rewards.com",
  "jadeglobal.hubble-rewards.com",
  "hnkpromotions.hubble-rewards.com",
  "nividrewards.hubble-rewards.com",
  "nividrewards-pvtltd.hubble-rewards.com",
  "joyperk-digital.hubble-rewards.com",
  "teamrustik.hubble-rewards.com",
  "godrej-consumerproduct.hubble-rewards.com",
  "godrejboyce-manufacturing.hubble-rewards.com",
  "godrejboyce-manufacturingco.hubble-rewards.com",
  "imcc.hubble-rewards.com",
  "northlandholding.hubble-rewards.com",
  "atgbusiness.hubble-rewards.com",
  "girnarinsurance.hubble-rewards.com",
  "cjshah.hubble-rewards.com",
  "princetonblue.hubble-rewards.com",
  "xceedanceconsulting.hubble-rewards.com",
  "life-workswellbeing.hubble-rewards.com",
  "holistique-beautyproducts.hubble-rewards.com",
]);
